import type { TargetareUniversRand } from "@/types/targetare";
import { SURSE_CONTACT } from "@/utils/targetareSurse";

export type RandRaport = Record<string, unknown>;

export type FiltruInterval = { min?: number; max?: number };

export type TipColoana = "text" | "numar" | "lista" | "bool" | "multi";

export type ColoanaTabel<R extends RandRaport = RandRaport> = {
  id: keyof R & string;
  eticheta: string;
  tip: TipColoana;
  latime: number;
  implicit: boolean;
  optiuni?: readonly string[];
  etichete?: Record<string, string>;
  sursa?: string;
  sursaId?: keyof R & string;
};

export const ETICHETE_ACTIONARIAT: Record<string, string> = {
  privat: "Privat",
  mixt: "Mixt (stat + privat)",
  stat_central: "Stat (central)",
  stat_local: "Stat (local)",
  regie_autonoma: "Regie autonomă",
};

export const ETICHETE_NIVEL_RISC: Record<string, string> = {
  esential: "Esențial",
  important: "Important",
  de_baza: "De bază",
  neevaluat: "Neevaluat",
};

export const ETICHETE_INCADRARE: Record<string, string> = {
  ESSENTIAL: "Esențială",
  IMPORTANT: "Importantă",
  OUT_OF_SCOPE: "În afara scopului",
};

export const ETICHETE_DIMENSIUNE: Record<string, string> = {
  mare: "Mare",
  mijlocie: "Mijlocie",
  mica: "Mică",
  micro: "Micro",
};

export const ETICHETE_TIER: Record<string, string> = {
  A: "A · CAEN principal + dimensiune",
  B: "B · listă oficială + dimensiune",
  C: "C · serviciu indiferent de dimensiune",
  D: "D · doar coduri secundare",
};

export const ETICHETE_ORIGINE: Record<string, string> = {
  clasificare: "Clasificare (CAEN, liste)",
  nis2pass: "Platforma NIS2PASS",
};

const SURSA_ONRC =
  "ONRC, exportul data.gov.ro „Firme înregistrate la Registrul Comerțului până la data de 02.09.2026”";
const SURSA_MF = "Ministerul Finanțelor, situații financiare 2025";
const SURSA_CLASIFICARE =
  "Clasificare NIS2PASS (CAEN autorizate + liste oficiale)";
const SURSA_RISC =
  "Estimare ENIRE/CyFun: matricea implicită a sectorului × dimensiunea din bilanț (maximul dintre sectoare)";
const SURSA_SURSE =
  "Sursele din care există date pentru firmă (contacte, persoane, Termene, platformă)";

const SURSE_FIXE: Partial<Record<keyof TargetareUniversRand, string>> = {
  cui: SURSA_ONRC,
  denumire: SURSA_ONRC,
  judet: SURSA_ONRC,
  localitate: SURSA_ONRC,
  adresa: SURSA_ONRC,
  cod_postal: SURSA_ONRC,
  forma_juridica: SURSA_ONRC,
  insolventa: SURSA_ONRC,
  web: SURSA_ONRC,
  salariati: SURSA_MF,
  cifra_afaceri: SURSA_MF,
  active_totale: SURSA_MF,
  dimensiune_stricta: SURSA_MF,
  aproape_de_prag: SURSA_MF,
  tier: SURSA_CLASIFICARE,
  clasificare_estimata: SURSA_CLASIFICARE,
  sectoare: SURSA_CLASIFICARE,
  servicii_principal: SURSA_CLASIFICARE,
  scor: SURSA_CLASIFICARE,
  origine: SURSA_CLASIFICARE,
  nivel_risc: SURSA_RISC,
  scor_risc: SURSA_RISC,
  actionariat: "Reguli pe denumire + acționarii din Termene.ro",
  emailuri_valide: "Contacte colectate (toate sursele)",
  nr_persoane: "Persoane colectate (toate sursele)",
  termene: "Termene.ro (răspuns complet salvat)",
  surse: SURSA_SURSE,
  sursa_onrc: SURSA_SURSE,
  sursa_lista: SURSA_SURSE,
  sursa_site: SURSA_SURSE,
  sursa_web: SURSA_SURSE,
  sursa_termene: SURSA_SURSE,
  sursa_import: SURSA_SURSE,
  sursa_manual: SURSA_SURSE,
  sursa_nis2pass: SURSA_SURSE,
};

type ColoanaFirma = ColoanaTabel<TargetareUniversRand>;

const coloanaSursa = (
  id: keyof TargetareUniversRand,
  eticheta: string,
  tip: TipColoana = "lista"
): ColoanaFirma => ({
  id,
  eticheta,
  tip,
  latime: 110,
  implicit: false,
  optiuni: tip === "lista" ? SURSE_CONTACT : undefined,
});

const coloanaSursaBool = (
  id: keyof TargetareUniversRand,
  eticheta: string
): ColoanaFirma => ({
  id,
  eticheta,
  tip: "bool",
  latime: 100,
  implicit: false,
});

const COLOANE_BAZA: ColoanaFirma[] = [
  { id: "cui", eticheta: "CUI", tip: "text", latime: 100, implicit: true },
  {
    id: "denumire",
    eticheta: "Denumire",
    tip: "text",
    latime: 260,
    implicit: true,
  },
  { id: "judet", eticheta: "Județ", tip: "lista", latime: 120, implicit: true },
  {
    id: "localitate",
    eticheta: "Localitate",
    tip: "text",
    latime: 150,
    implicit: true,
  },
  {
    id: "adresa",
    eticheta: "Adresă",
    tip: "text",
    latime: 220,
    implicit: false,
  },
  {
    id: "cod_postal",
    eticheta: "Cod poștal",
    tip: "text",
    latime: 90,
    implicit: false,
  },
  {
    id: "site",
    eticheta: "Site",
    tip: "text",
    latime: 190,
    implicit: true,
    sursaId: "site_sursa",
  },
  coloanaSursa("site_sursa", "Sursă site"),
  {
    id: "email_principal",
    eticheta: "Email",
    tip: "text",
    latime: 240,
    implicit: true,
    sursaId: "email_principal_sursa",
  },
  coloanaSursa("email_principal_sursa", "Sursă email"),
  {
    id: "telefon_principal",
    eticheta: "Telefon",
    tip: "text",
    latime: 150,
    implicit: true,
    sursaId: "telefon_principal_sursa",
  },
  coloanaSursa("telefon_principal_sursa", "Sursă telefon"),
  {
    id: "emailuri_valide",
    eticheta: "Nr. emailuri",
    tip: "numar",
    latime: 90,
    implicit: false,
  },
  {
    id: "tier",
    eticheta: "Tier",
    tip: "lista",
    latime: 70,
    implicit: true,
    optiuni: ["A", "B", "C", "D"],
    etichete: ETICHETE_TIER,
  },
  {
    id: "clasificare_estimata",
    eticheta: "Încadrare",
    tip: "lista",
    latime: 120,
    implicit: true,
    optiuni: ["ESSENTIAL", "IMPORTANT", "OUT_OF_SCOPE"],
    etichete: ETICHETE_INCADRARE,
  },
  {
    id: "nivel_risc",
    eticheta: "Nivel de risc",
    tip: "lista",
    latime: 120,
    implicit: true,
    optiuni: ["esential", "important", "de_baza"],
    etichete: ETICHETE_NIVEL_RISC,
  },
  {
    id: "scor_risc",
    eticheta: "Scor risc",
    tip: "numar",
    latime: 90,
    implicit: false,
  },
  {
    id: "dimensiune_stricta",
    eticheta: "Dimensiune",
    tip: "lista",
    latime: 100,
    implicit: true,
    optiuni: ["mare", "mijlocie", "mica", "micro"],
    etichete: ETICHETE_DIMENSIUNE,
  },
  {
    id: "aproape_de_prag",
    eticheta: "Aproape de prag",
    tip: "bool",
    latime: 100,
    implicit: false,
  },
  {
    id: "actionariat",
    eticheta: "Acționariat",
    tip: "lista",
    latime: 120,
    implicit: true,
    optiuni: ["privat", "mixt", "stat_central", "stat_local", "regie_autonoma"],
    etichete: ETICHETE_ACTIONARIAT,
  },
  {
    id: "origine",
    eticheta: "Origine în univers",
    tip: "lista",
    latime: 140,
    implicit: false,
    optiuni: ["clasificare", "nis2pass"],
    etichete: ETICHETE_ORIGINE,
  },
  {
    id: "salariati",
    eticheta: "Salariați",
    tip: "numar",
    latime: 90,
    implicit: true,
  },
  {
    id: "cifra_afaceri",
    eticheta: "Cifra de afaceri (lei)",
    tip: "numar",
    latime: 140,
    implicit: true,
  },
  {
    id: "active_totale",
    eticheta: "Active totale (lei)",
    tip: "numar",
    latime: 140,
    implicit: false,
  },
  {
    id: "sectoare",
    eticheta: "Sectoare",
    tip: "multi",
    latime: 200,
    implicit: true,
  },
  {
    id: "servicii_selectate",
    eticheta: "Servicii selectate (scor ≥ prag)",
    tip: "multi",
    latime: 180,
    implicit: true,
  },
  {
    id: "servicii_scor",
    eticheta: "Scoruri pe servicii",
    tip: "text",
    latime: 260,
    implicit: false,
  },
  {
    id: "servicii_principal",
    eticheta: "Servicii (CAEN principal)",
    tip: "multi",
    latime: 160,
    implicit: false,
  },
  {
    id: "servicii_secundar",
    eticheta: "Servicii (cod autorizat)",
    tip: "multi",
    latime: 160,
    implicit: false,
  },
  {
    id: "servicii_liste",
    eticheta: "Servicii (liste)",
    tip: "multi",
    latime: 160,
    implicit: false,
  },
  {
    id: "risc_detalii",
    eticheta: "Risc pe sectoare",
    tip: "text",
    latime: 240,
    implicit: false,
  },
  {
    id: "conducere",
    eticheta: "Conducere",
    tip: "text",
    latime: 260,
    implicit: false,
    sursaId: "conducere_sursa",
  },
  coloanaSursa("conducere_sursa", "Sursă conducere", "text"),
  {
    id: "director_it",
    eticheta: "Responsabil IT / securitate",
    tip: "text",
    latime: 180,
    implicit: true,
    sursaId: "director_it_sursa",
  },
  coloanaSursa("director_it_sursa", "Sursă responsabil IT"),
  {
    id: "email_persoana",
    eticheta: "Email persoană",
    tip: "text",
    latime: 220,
    implicit: false,
    sursaId: "email_persoana_sursa",
  },
  coloanaSursa("email_persoana_sursa", "Sursă email persoană"),
  {
    id: "nr_persoane",
    eticheta: "Nr. persoane",
    tip: "numar",
    latime: 90,
    implicit: false,
  },
  {
    id: "termene",
    eticheta: "Date Termene",
    tip: "bool",
    latime: 90,
    implicit: false,
  },
  {
    id: "surse",
    eticheta: "Surse de date",
    tip: "multi",
    latime: 180,
    implicit: false,
    optiuni: SURSE_CONTACT,
  },
  coloanaSursaBool("sursa_onrc", "Sursă: ONRC"),
  coloanaSursaBool("sursa_lista", "Sursă: Liste oficiale"),
  coloanaSursaBool("sursa_site", "Sursă: Site firmă"),
  coloanaSursaBool("sursa_web", "Sursă: Internet"),
  coloanaSursaBool("sursa_termene", "Sursă: Termene.ro"),
  coloanaSursaBool("sursa_import", "Sursă: Import CSV"),
  coloanaSursaBool("sursa_manual", "Sursă: Manual"),
  coloanaSursaBool("sursa_nis2pass", "Sursă: Platforma NIS2PASS"),
  { id: "scor", eticheta: "Scor", tip: "numar", latime: 70, implicit: false },
  {
    id: "forma_juridica",
    eticheta: "Formă juridică",
    tip: "lista",
    latime: 90,
    implicit: false,
  },
  {
    id: "insolventa",
    eticheta: "Insolvență",
    tip: "bool",
    latime: 90,
    implicit: false,
  },
  {
    id: "web",
    eticheta: "Web (ONRC)",
    tip: "text",
    latime: 150,
    implicit: false,
  },
];

export const COLOANE_TABEL: ColoanaFirma[] = COLOANE_BAZA.map((c) => ({
  ...c,
  sursa: c.sursa ?? SURSE_FIXE[c.id],
}));

const normalizeaza = (v: unknown): string =>
  String(v ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

export function potrivesteText(valoare: unknown, filtru: unknown): boolean {
  const cautat = normalizeaza(filtru).trim();
  if (!cautat) return true;
  if (cautat === "*" || cautat === "-") {
    return potrivesteBool(valoare, cautat === "*" ? "da" : "nu");
  }
  if (Array.isArray(valoare)) {
    return valoare.some((x) => normalizeaza(x).includes(cautat));
  }
  return normalizeaza(valoare).includes(cautat);
}

export function potrivesteLista(valoare: unknown, filtru: unknown): boolean {
  if (!Array.isArray(filtru) || filtru.length === 0) return true;
  if (Array.isArray(valoare)) {
    return valoare.some((x) => filtru.includes(String(x)));
  }
  return filtru.includes(String(valoare ?? ""));
}

export function potrivesteInterval(valoare: unknown, filtru: unknown): boolean {
  const f = (filtru ?? {}) as FiltruInterval;
  if (f.min === undefined && f.max === undefined) return true;
  const n = typeof valoare === "number" ? valoare : Number(valoare);
  if (valoare === null || valoare === undefined || Number.isNaN(n)) {
    return false;
  }
  if (f.min !== undefined && n < f.min) return false;
  if (f.max !== undefined && n > f.max) return false;
  return true;
}

export function potrivesteBool(valoare: unknown, filtru: unknown): boolean {
  if (filtru === undefined || filtru === null || filtru === "") return true;
  const dorit = filtru === true || filtru === "da";
  const are = Array.isArray(valoare)
    ? valoare.length > 0
    : valoare === true ||
      (typeof valoare === "number" && valoare > 0) ||
      (typeof valoare === "string" && valoare.length > 0);
  return are === dorit;
}

export function valoareCelulaText<R extends RandRaport>(
  rand: R,
  id: keyof R & string
): string {
  const v = rand[id];
  if (v === null || v === undefined) return "";
  if (Array.isArray(v)) return v.join("; ");
  if (typeof v === "boolean") return v ? "da" : "nu";
  if (typeof v === "object") return textDinObiect(v as Record<string, unknown>);
  return String(v);
}

export function textDinObiect(obiect: Record<string, unknown>): string {
  const intrari = Object.entries(obiect);
  const scoruri = intrari.filter(
    ([, x]) => x && typeof x === "object" && "scor" in (x as object)
  );
  if (scoruri.length > 0) {
    return scoruri
      .map(([k, x]) => {
        const s = x as { scor?: unknown; selectat?: unknown };
        return `${k}: ${String(s.scor ?? "")}${s.selectat ? " ✓" : ""}`;
      })
      .join("; ");
  }
  if (
    "sectoare" in obiect &&
    obiect.sectoare &&
    typeof obiect.sectoare === "object"
  ) {
    return Object.entries(obiect.sectoare as Record<string, unknown>)
      .map(([k, x]) => `${k}: ${String(x)}`)
      .join("; ");
  }
  return intrari
    .map(
      ([k, x]) =>
        `${k}: ${typeof x === "object" ? JSON.stringify(x) : String(x)}`
    )
    .join("; ");
}

export function randuriPentruExport<R extends RandRaport>(
  randuri: R[],
  coloane: ColoanaTabel<R>[]
): Record<string, string>[] {
  return randuri.map((r) =>
    Object.fromEntries(
      coloane.map((c) => [c.eticheta, valoareCelulaText(r, c.id)])
    )
  );
}

export function csvDinRanduri(
  randuri: Record<string, string>[],
  coloane: string[]
): string {
  const celula = (s: string) =>
    /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  const linii = [
    coloane.join(","),
    ...randuri.map((r) => coloane.map((c) => celula(r[c] ?? "")).join(",")),
  ];
  return "﻿" + linii.join("\r\n");
}
